// Hooks de queries para autenticación con React Query
// Maneja la obtención y sincronización del estado de autenticación

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { getCurrentUser } from '../services/authService';

// Hook para obtener la sesión actual del usuario
export const useAuthQuery = () => {
  // Query para obtener la sesión actual
  const sessionQuery = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: async () => {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        throw new Error(error.message);
      }

      return { session, user: session?.user || null };
    },
    staleTime: Infinity, // Nunca considerar stale - solo invalidar explícitamente
    gcTime: 1000 * 60 * 120, // 2 horas (cache más persistente)
    refetchOnWindowFocus: false, // No refrescar al cambiar de pestaña
    refetchOnReconnect: false, // Desactivar refresh al reconectar
    retry: 1, // Solo un reintento para evitar peticiones constantes
    retryDelay: 2000, // 2 segundos entre reintentos (más lento)
  });

  // Query para obtener el perfil del usuario (si existe tabla de perfiles)
  const userProfileQuery = useQuery({
    queryKey: ['auth', 'userProfile'],
    queryFn: async () => {
      // Esta query solo se ejecuta si hay una sesión activa
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        return null;
      }

      // Aquí podrías obtener datos adicionales del usuario desde tu base de datos
      // Por ejemplo, si tienes una tabla de perfiles:
      // const { data, error } = await supabase
      //   .from('user_profiles')
      //   .select('*')
      //   .eq('user_id', session.user.id)
      //   .single();
      
      // Por ahora retornamos los datos básicos del usuario
      return {
        id: session.user.id,
        email: session.user.email,
        created_at: session.user.created_at,
        // Agrega aquí campos adicionales de tu tabla de perfiles
      };
    },
    enabled: !!sessionQuery.data?.session, // Solo ejecutar si hay sesión
    staleTime: 1000 * 60 * 45, // 45 minutos - aumentamos para reducir frecuencia
    gcTime: 1000 * 60 * 90, // 1.5 horas
    refetchOnWindowFocus: false, // No refrescar al cambiar de pestaña
    refetchOnReconnect: true, // Solo refrescar al reconectar
    retry: 1, // Solo un reintento
    retryDelay: 1000, // 1 segundo entre reintentos
  });

  return {
    sessionQuery,
    userProfileQuery,
  };
};

/**
 * Hook para obtener información del usuario actual
 * @param {object} options - Opciones adicionales del query
 * @returns {object} - Query de TanStack Query
 */
export const useCurrentUserQuery = (options = {}) => {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutos
    onSuccess: (data) => {
      console.log('Usuario obtenido exitosamente');
      options.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('Error obteniendo usuario:', error);
      options.onError?.(error);
    },
    ...options
  });
};
